import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { PageHeader, Empty } from "@/components/Page";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Package, Plus, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export function ProductDialog({ open, onOpenChange, onSaved, defaultCategory }) {
  const [plants, setPlants] = useState([]);
  const [form, setForm] = useState({ category: defaultCategory || "cement", status: "active" });
  useEffect(() => {
    if (open) api.get("/plants").then(({ data }) => setPlants(data));
    setForm({ category: defaultCategory || "cement", status: "active" });
  }, [open, defaultCategory]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    try {
      const { data } = await api.post("/products", form);
      toast.success("Product created");
      onOpenChange(false); onSaved && onSaved(data);
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>New product</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-xs">Category *</Label>
            <Select value={form.category} onValueChange={(v) => set("category", v)}>
              <SelectTrigger data-testid="product-category" className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="cement">Cement</SelectItem>
                <SelectItem value="concrete">Ready-Mix Concrete</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Plant *</Label>
            <Select value={form.plant_id} onValueChange={(v) => set("plant_id", v)}>
              <SelectTrigger data-testid="product-plant" className="mt-1"><SelectValue placeholder="Select plant" /></SelectTrigger>
              <SelectContent>
                {plants.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Product name *</Label><Input data-testid="product-name" value={form.name || ""} onChange={(e) => set("name", e.target.value)} className="mt-1" /></div>
          <div><Label className="text-xs">Trade name</Label><Input data-testid="product-trade" value={form.trade_name || ""} onChange={(e) => set("trade_name", e.target.value)} className="mt-1" /></div>
          <div><Label className="text-xs">Standard / designation</Label><Input data-testid="product-standard" value={form.standard || ""} onChange={(e) => set("standard", e.target.value)} className="mt-1" placeholder={form.category === "cement" ? "EN 197-1 / NIS 444" : "EN 206 / NIS 87"} /></div>
          <div><Label className="text-xs">Market / geographical scope</Label><Input data-testid="product-market" value={form.market_scope || ""} onChange={(e) => set("market_scope", e.target.value)} className="mt-1" placeholder="Nigeria" /></div>
          <div><Label className="text-xs">Description</Label><Textarea data-testid="product-desc" value={form.description || ""} onChange={(e) => set("description", e.target.value)} className="mt-1" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="product-save" onClick={save} disabled={!form.name || !form.plant_id}>Create product</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Products() {
  const [products, setProducts] = useState(null);
  const [plants, setPlants] = useState(null);
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const load = () => { api.get("/products").then(({ data }) => setProducts(data)); api.get("/plants").then(({ data }) => setPlants(data)); };
  useEffect(() => { load(); }, []);
  const noPlants = plants && plants.length === 0;
  return (
    <div>
      <PageHeader title="Products" subtitle="Cement and ready-mix concrete products for which you produce EPDs."
        action={<Button data-testid="add-product-btn" onClick={() => setOpen(true)}><Plus className="h-4 w-4 mr-1.5" /> Add product</Button>} />
      {products && products.length === 0 ? (
        noPlants ? (
          <Empty icon={Package} title="No products yet" hint="Add the cement or ready-mix concrete product for which you want to create an EPD. First, add the plant that produces it."
            action={<Button data-testid="empty-add-plant-first" onClick={() => nav("/app/plants")}><ArrowRight className="h-4 w-4 mr-1.5" /> Add a plant first</Button>} />
        ) : (
          <Empty icon={Package} title="No products yet" hint="Add the cement or ready-mix concrete product for which you want to create an EPD."
            action={<Button data-testid="empty-add-product" onClick={() => setOpen(true)}><Plus className="h-4 w-4 mr-1.5" /> Add your first product</Button>} />
        )
      ) : (
        <div className="bg-white border rounded-lg overflow-hidden">
          <table className="w-full text-sm epn-table">
            <thead className="bg-slate-50 border-b"><tr className="text-left">
              <th className="px-5 py-2.5">Product</th><th className="px-5 py-2.5">Category</th>
              <th className="px-5 py-2.5">Plant</th><th className="px-5 py-2.5">Declared unit</th>
              <th className="px-5 py-2.5">Standard</th><th className="px-5 py-2.5">Status</th>
            </tr></thead>
            <tbody>
              {(products || []).map((p) => (
                <tr key={p.id} data-testid={`product-row-${p.id}`} className="border-b last:border-0">
                  <td className="px-5 py-3 font-medium">{p.name}{p.is_demo && <span className="ml-2 text-[10px] rounded bg-amber-50 border border-amber-200 text-amber-700 px-1.5 py-0.5">DEMO</span>}</td>
                  <td className="px-5 py-3 text-slate-600">{p.category === "cement" ? "Cement" : "Ready-Mix"}</td>
                  <td className="px-5 py-3 text-slate-600">{p.plant_name}</td>
                  <td className="px-5 py-3 font-mono text-xs">{p.declared_unit}</td>
                  <td className="px-5 py-3 text-slate-600">{p.standard}</td>
                  <td className="px-5 py-3"><span className="text-xs rounded border px-2 py-0.5 bg-slate-50">{p.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <ProductDialog open={open} onOpenChange={setOpen} onSaved={load} />
    </div>
  );
}
